/**
 * SetNextWaypointAction - behavior tree action for waypoint progression.
 */

import { EngineClass } from '../../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../../BehaviorNode.js';
import { BehaviorAction, type BehaviorActionOptions } from '../../BehaviorAction.js';

import type { Blackboard } from '../../../blackboard/Blackboard.js';
import type * as THREE from 'three';

export interface SetNextWaypointActionOptions extends BehaviorActionOptions {
  waypointsKey?: string;
  indexKey?: string;
  currentWaypointKey?: string;
  loop?: boolean;
}

@EngineClass('SetNextWaypointAction')
export class SetNextWaypointAction extends BehaviorAction {
  private waypointsKey: string;
  private indexKey: string;
  private currentWaypointKey: string;
  private loop: boolean;

  constructor(options: SetNextWaypointActionOptions = {}) {
    super({
      name: 'SetNextWaypoint',
      ...options
    });

    this.waypointsKey = options.waypointsKey ?? 'PatrolWaypoints';
    this.indexKey = options.indexKey ?? 'CurrentWaypointIndex';
    this.currentWaypointKey = options.currentWaypointKey ?? 'CurrentWaypoint';
    this.loop = options.loop ?? true;
  }

  protected override onInitialize(blackboard: Blackboard): void {
    this.updateCurrentWaypoint(blackboard);
  }

  protected override onEnter(blackboard: Blackboard): void {
    const waypoints = blackboard.getValue<THREE.Vector3[]>(this.waypointsKey);
    if (!waypoints || waypoints.length === 0) {
      if (!waypoints) {
        console.warn(`SetNextWaypointAction: No waypoints found for blackboard key '${this.waypointsKey}'`);
      } else {
        console.warn(`SetNextWaypointAction: Waypoints array is empty for key '${this.waypointsKey}'`);
      }
      return;
    }


  }

  protected override async onUpdate(blackboard: Blackboard, deltaTime: number): Promise<BehaviorStatus> {
    const waypoints = blackboard.getValue<THREE.Vector3[]>(this.waypointsKey);
    if (!waypoints || waypoints.length === 0) {
      if (!waypoints) {
        console.warn(`SetNextWaypointAction: No waypoints found for blackboard key '${this.waypointsKey}'`);
      } else {
        console.warn(`SetNextWaypointAction: Waypoints array is empty for key '${this.waypointsKey}'`);
      }
      return BehaviorStatus.Failure;
    }

    // Check if this is the first time setting a waypoint
    const currentWaypoint = blackboard.getValue(this.currentWaypointKey);
    if (!currentWaypoint) {
      // First time - set the current waypoint without advancing index
      this.updateCurrentWaypoint(blackboard);
      return BehaviorStatus.Success;
    }

    // This is waypoint advancement - move to next waypoint
    let currentIndex = blackboard.getValue<number>(this.indexKey) ?? 0;

    // Move to next waypoint
    currentIndex++;

    // Handle looping or completion
    if (currentIndex >= waypoints.length) {
      if (this.loop) {
        currentIndex = 0; // Loop back to start
      } else {
        // Non-looping patrol is complete, reset to first waypoint
        currentIndex = 0;
      }
    }

    // Update blackboard with new index
    blackboard.setValue(this.indexKey, currentIndex);

    // Update current waypoint position
    this.updateCurrentWaypoint(blackboard);

    return BehaviorStatus.Success;
  }

  private updateCurrentWaypoint(blackboard: Blackboard): void {
    const waypoints = blackboard.getValue<THREE.Vector3[]>(this.waypointsKey);
    const currentIndex = blackboard.getValue<number>(this.indexKey) ?? 0;

    if (waypoints && waypoints.length > 0 && currentIndex < waypoints.length) {
      const currentWaypoint = waypoints[currentIndex];
      blackboard.setValue(this.currentWaypointKey, currentWaypoint);
    }
  }
}
